import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient, onlineManager } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Lock, Loader2, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/Logo'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/app/store/auth'
import { useLockStore } from '@/app/store/lock'
import { usePresence } from '@/contexts/PresenceContext'
import { authApi } from '@/lib/api/clients/auth'
import {
  EnterpriseWebAuthnAPI, authenticateEnterprisePasskey,
  getWebAuthnErrorMessage, isWebAuthnSupported,
} from '@/lib/security/webauthn-enterprise'
import { appLocale } from '@/lib/dateUtils'

/**
 * Locks the screen after a period of inactivity, WITHOUT ending the session.
 *
 * Signing out ends the session and loses the view — wrong for a screen sitting in a recovery
 * operations room mid-incident. Locking hides what is on screen and asks who is at the keyboard.
 * The session survives; the estate is not readable by whoever wanders past.
 *
 * UNLOCKING IS A LOGIN, NOT A LOOKALIKE. It calls the same endpoints the sign-in page calls, in
 * the same order, and needs the same credentials: password, then TOTP or passkey where the account
 * requires them. An earlier draft added a dedicated /api/session/unlock that checked only the
 * password — a second authentication path, which is how the side door ends up weaker than the
 * front one. It was deleted rather than kept alongside this.
 *
 * IT DOES NOT NAVIGATE. The real flow sets mfaRequired on the store and the router sends you to
 * /mfa, which unmounts the application and loses exactly the view this exists to protect. So the
 * endpoints are called directly and the store is only touched on success, with setTokens. Nothing
 * here writes mfaRequired.
 *
 * YOU UNLOCK AS YOURSELF. The email is the signed-in user's and is not editable — unlocking into
 * someone else's view is not a thing that should be possible. A different person signs out first.
 *
 * WHAT THIS IS NOT: a server-side session suspension. The session stays valid while locked, which
 * is the point. Someone with devtools and the session token can already act as that user, and
 * removing an overlay is not their easiest route. This is a lock against an unattended keyboard,
 * and saying so plainly matters — a lock described as more than it is gets trusted for more.
 *
 * The overlay carries NOTHING from the application. A screensaver that shows what it is hiding is
 * not one.
 */

type Step = 'password' | 'mfa'

export function Screensaver({ active, onUnlocked }: { active: boolean; onUnlocked: () => void }) {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => new Date())
  const [step, setStep] = useState<Step>('password')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [methods, setMethods] = useState<string[]>([])
  // Answering with a code instead of the passkey: only ever the user's choice (see the MFA step).
  const [useCode, setUseCode] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()
  const { setMyStatus } = usePresence()
  const user = useAuthStore((s) => s.user)
  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)
  const authLogout = useAuthStore((s) => s.logout)
  const manual = useLockStore((s) => s.manual)
  // Clearing the lock alongside the session: a persisted lock outliving its session would greet
  // the next person with a lock screen for an account nobody is signed into.
  const logout = useCallback(() => { useLockStore.getState().unlock(); authLogout() }, [authLogout])

  const email = user?.email ?? ''
  const hasPasskey = methods.includes('webauthn')
    || Boolean(user?.mfa?.webauthnEnabled || (user as any)?.webauthnEnabled)
  const hasCode = methods.includes('totp') || !hasPasskey
  // The passkey leads, as on the sign-in page; the code is a link beneath it.
  const showCode = hasCode && (!hasPasskey || useCode)

  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [active])

  /**
   * Colleagues see AWAY while the screen is locked.
   *
   * The socket stays open behind the overlay, so without this the person shows as available —
   * and someone rings a screen nobody is sitting at. Declared rather than inferred: only this
   * tab knows it is locked, and the server takes the most available status across a user's tabs,
   * so a second screen still in use keeps them available.
   */
  useEffect(() => {
    setMyStatus(active ? 'away' : 'available')
  }, [active, setMyStatus])

  /**
   * NOTHING FETCHES WHILE LOCKED.
   *
   * Behind the overlay every poller carried on — the recovery Gantt every 30s, ingress every 10s,
   * the dashboard, the sidebar. A screen locked overnight is thousands of requests nobody is
   * reading, and once the 15-minute access token expires they are thousands of 401s, each one
   * asking the interceptor to refresh. That is what could end the session while the person was
   * away from a screen they had deliberately left running.
   *
   * onlineManager is React Query's own switch for "the application is not live right now" and it
   * pauses refetchInterval as well as focus refetches, which pausing focus alone does not.
   * Restored on unlock, where invalidateQueries then brings everything up to date at once.
   */
  useEffect(() => {
    if (!active) return
    onlineManager.setOnline(false)
    return () => onlineManager.setOnline(true)
  }, [active])

  // Reset on every lock: a password typed before must not survive, and a previous attempt's
  // half-finished MFA step must not be resumed.
  useEffect(() => {
    if (!active) return
    setStep('password'); setPassword(''); setCode(''); setError(null); setMethods([]); setUseCode(false)
    const id = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(id)
  }, [active])

  /**
   * The one place tokens are accepted — every path ends here or fails.
   *
   * IT RE-READS /auth/me, and that is not belt-and-braces. A lock can outlive the 15-minute
   * access token easily: background polling keeps running behind the overlay, those requests 401,
   * and the axios interceptor's refresh can fail and call forceLogout — which clears `user` and
   * `isAuthenticated`. setTokens writes ONLY tokens, so unlocking then left the application
   * authenticated by token but with no user: every hasRole check false, the sidebar down to its
   * one ungated section, and admin pages refusing their own admin. On top of that, useMe caches
   * with retry:false, so the /auth/me that failed during the lock never retried on its own.
   *
   * NOT the login response's user, which carries roles as plain strings and no permissions at
   * all. /auth/me is the authoritative shape and the one the rest of the app is built on.
   *
   * If it fails with tokens this fresh, something is genuinely wrong — signing out is the honest
   * answer, rather than unlocking into an application that will refuse its own pages.
   */
  const finish = useCallback(async (data: any) => {
    if (!data?.accessToken || !data?.refreshToken) return false
    setTokens(data.accessToken, data.refreshToken, 15 * 60)
    try {
      const me = await authApi.me()
      const body = me.data as any
      const full = body?.user ?? body?.data?.user
      if (!full) throw new Error('no user')
      setUser(full)
    } catch {
      setError(t('screensaver.sessionGone', 'Your session could not be restored. Please sign in again.'))
      logout()
      return true
    }
    // Everything behind the overlay has been failing while locked; those caches hold errors and
    // stale data, and retry:false means several will not recover on their own.
    void queryClient.invalidateQueries()
    setPassword(''); setCode(''); setError(null)
    onUnlocked()
    return true
  }, [setTokens, setUser, onUnlocked, queryClient, t])

  const submitPassword = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (busy || !password || !email) return
    setBusy(true); setError(null)
    try {
      const res = await authApi.login({ email, password } as any)
      const data = (res.data as any)?.data ?? res.data
      if (await finish(data)) return
      if (data?.mfaRequired) {
        // Deliberately NOT setMfaRequired — that navigates to /mfa and unmounts the view.
        setMethods(Array.isArray(data.methods) ? data.methods : [])
        setStep('mfa')
        setPassword('')
        setTimeout(() => inputRef.current?.focus(), 50)
        return
      }
      setError(t('screensaver.wrong', 'Incorrect password.'))
    } catch (err: any) {
      setError(err?.response?.status === 429
        ? t('screensaver.tooMany', 'Too many attempts. Try again shortly, or sign out.')
        : t('screensaver.wrong', 'Incorrect password.'))
      setPassword('')
    } finally {
      setBusy(false)
    }
  }, [busy, password, email, finish, t])

  const submitCode = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (busy || code.length < 6) return
    setBusy(true); setError(null)
    try {
      const res = await authApi.totpVerify({ token: code })
      const data = (res.data as any)?.data ?? res.data
      if (!(await finish(data))) setError(t('screensaver.badCode', 'That code was not accepted.'))
    } catch {
      setError(t('screensaver.badCode', 'That code was not accepted.'))
      setCode('')
    } finally {
      setBusy(false)
    }
  }, [busy, code, email, finish, t])

  const submitPasskey = useCallback(async () => {
    if (busy || !email) return
    if (!isWebAuthnSupported()) {
      setError(t('screensaver.noPasskeySupport', 'This browser cannot use a passkey.'))
      return
    }
    setBusy(true); setError(null)
    try {
      const api = new EnterpriseWebAuthnAPI()
      const options = await api.beginAuthentication()
      const credential = await authenticateEnterprisePasskey(options)
      const result = await api.completeAuthentication(credential)
      const data = (result as any)?.data ?? result
      if (!(await finish(data))) setError(t('screensaver.passkeyFailed', 'Passkey was not accepted.'))
    } catch (err: any) {
      setError(getWebAuthnErrorMessage?.(err) || t('screensaver.passkeyFailed', 'Passkey was not accepted.'))
    } finally {
      setBusy(false)
    }
  }, [busy, email, finish, t])

  if (!active) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('screensaver.label', 'Screen locked')}
      className="fixed inset-0 z-[9999] flex select-none flex-col items-center justify-center bg-background"
    >
      <Logo className="h-16" />
      <p className="mt-4 text-center text-5xl font-light tabular-nums tracking-tight">
        {now.toLocaleTimeString(appLocale(), { hour: '2-digit', minute: '2-digit' })}
      </p>
      <p className="mt-1 text-center text-sm text-muted-foreground">
        {now.toLocaleDateString(appLocale(), { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>

      <div className="mt-10 w-full max-w-xs">
        {/* SAYS LOCKED, AND LOOKS UNLIKE THE SIGN-IN PAGE — deliberately, and it is the
            convention every desktop follows. A screen that looks like sign-in tells the user
            they were signed out: they go looking for work they think they lost, and they try a
            different account because sign-in is where you choose one. Naming the state, showing
            whose session it is, and fixing the account to that person removes all three. */}
        {/* The WORDS are centred and the padlock hangs off their leading edge. Centred as one
            group, the icon and its gap pushed the words 12px right of everything else in the
            column, and this line sits directly above the password field. */}
        <p className="mb-1 text-center text-base font-semibold">
          <span className="relative inline-block">
            <Lock className="absolute end-full top-1/2 me-2 h-4 w-4 -translate-y-1/2" />
            {t('screensaver.locked', 'Screen locked')}
          </span>
        </p>
        <p className="mb-3 text-center text-xs text-muted-foreground">
          {/* Saying WHY distinguishes a decision from a surprise: "I locked this" needs no
              explanation, "it locked itself" does, and the settings that control it are worth
              knowing about at exactly that moment. */}
          {manual
            ? t('screensaver.lockedManual', 'You locked this screen.')
            : t('screensaver.lockedIdle', 'Locked after a period of inactivity.')}
          {email ? ` ${email}` : ''}
        </p>

        {step === 'password' ? (
          <form onSubmit={submitPassword} className="space-y-2">
            {/* Left-aligned, like every sign-in field. Centred, the dots grew in both directions
                with each key and the caret sat at their right end, so typing leaned right. */}
            <Input
              ref={inputRef} type="password" autoComplete="current-password"
              placeholder={t('screensaver.passwordPlaceholder', 'Password')}
              aria-label={t('screensaver.passwordPlaceholder', 'Password')}
              value={password} disabled={busy}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" className="w-full" disabled={busy || !password}>
              {busy
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    {t('screensaver.unlocking', 'Unlocking…')}</>
                : t('screensaver.unlock', 'Unlock')}
            </Button>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-center text-xs text-muted-foreground">
              {t('screensaver.mfaPrompt', 'Confirm it is you, the same way you sign in.')}
            </p>
            {showCode ? (
              <form onSubmit={submitCode} className="space-y-2">
                <Input
                  ref={inputRef} inputMode="numeric" autoComplete="one-time-code" maxLength={8}
                  placeholder={t('screensaver.codePlaceholder', 'Authentication code')}
                  aria-label={t('screensaver.codePlaceholder', 'Authentication code')}
                  value={code} disabled={busy}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center tracking-[0.3em]"
                />
                <Button type="submit" className="w-full" disabled={busy || code.length < 6}>
                  {busy
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                        {t('screensaver.unlocking', 'Unlocking…')}</>
                    : t('screensaver.unlock', 'Unlock')}
                </Button>
                {hasPasskey && (
                  <Button type="button" variant="link" size="sm" className="w-full text-muted-foreground"
                          disabled={busy} onClick={() => { setUseCode(false); setCode(''); setError(null) }}>
                    {t('auth.mfaPage.usePasskeyInstead')}
                  </Button>
                )}
              </form>
            ) : (
              <>
                <Button className="w-full" disabled={busy} onClick={submitPasskey}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t('screensaver.usePasskey', 'Use a passkey')}
                </Button>
                {hasCode && (
                  <Button type="button" variant="link" size="sm" className="w-full text-muted-foreground"
                          disabled={busy}
                          onClick={() => { setUseCode(true); setError(null); setTimeout(() => inputRef.current?.focus(), 50) }}>
                    {t('auth.mfaPage.useAppInstead')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {error && <p className="mt-2 text-center text-xs text-destructive">{error}</p>}

        <Button variant="ghost" size="sm" className="mt-4 w-full text-muted-foreground"
                onClick={() => logout()}>
          {t('screensaver.signOut', 'Sign out')}
        </Button>
      </div>

      <p className="mt-8 max-w-xs text-balance text-center text-[11px] text-muted-foreground">
        {t('screensaver.stillSignedIn',
           'Your session is still open — unlocking returns you to exactly where you were.')}
      </p>
    </div>
  )
}
